import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  Quote,
  QuoteStatusEnum,
  CreateQuoteInput,
  UpdateQuoteInput,
  SendQuoteInput,
  ConvertQuoteToInvoiceInput,
  Customer,
  CreateCustomerInput,
  IssuerProfile,
  CreateIssuerProfileInput,
  UpdateIssuerProfileInput,
  LineItem
} from './types/quotes.types';

import type {
  Invoice
} from './types/invoices.types';

export interface ListQuotesParams {
  customerId?: number
  status?: QuoteStatusEnum
  enquiryId?: number
  limit?: number
  offset?: number
}

export async function listQuotes(
  params: ListQuotesParams = {},
  token: string
): Promise<PaginatedResponse<Quote>> {

  const query = new URLSearchParams();

  if (params.customerId !== undefined) {

    query.set('customerId', String(params.customerId));
  }

  if (params.status !== undefined) {

    query.set('status', params.status);
  }

  if (params.enquiryId !== undefined) {

    query.set('enquiryId', String(params.enquiryId));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Quote>>(`/quotes${ qs }`, undefined, token);
}

export async function getQuoteById(
  id: number,
  token: string
): Promise<ApiResponse<Quote>> {

  return apiRequest<ApiResponse<Quote>>(`/quotes/${ id }`, undefined, token);
}

export interface ListMyQuotesParams {
  limit?: number
  offset?: number
  // Scopes to one booking, so the detail page stops pulling every quote
  // the client has and matching in the browser.
  enquiryId?: number
}

/** Self-service. The caller's own quotes - empty if they have no
 * customer record yet, not an error. */
export async function getMyQuotes(
  params: ListMyQuotesParams = {},
  token: string
): Promise<PaginatedResponse<Quote>> {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  if (params.enquiryId !== undefined) {

    query.set('enquiryId', String(params.enquiryId));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Quote>>(`/quotes/me${ qs }`, undefined, token);
}

export interface AcceptMyQuoteResult {
  quote: Quote
  invoice: Invoice
}

/** Self-service. Accept the caller's own quote - 403s if it isn't
 * theirs. Runs the same accept -> convert -> send chain as the email
 * magic-link flow, then returns the resulting invoice directly so the
 * frontend can update without a refetch. */
export async function acceptMyQuote(
  id: number,
  token: string
): Promise<ApiResponse<AcceptMyQuoteResult>> {

  return apiRequest<ApiResponse<AcceptMyQuoteResult>>(
    `/quotes/${ id }/accept/me`,
    {
      method: 'POST'
    },
    token
  );
}

export async function createQuote(
  input: CreateQuoteInput,
  token: string
): Promise<ApiResponse<Quote>> {

  return apiRequest<ApiResponse<Quote>>(
    '/quotes',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateQuote(
  id: number,
  input: UpdateQuoteInput,
  token: string
): Promise<ApiResponse<Quote>> {

  return apiRequest<ApiResponse<Quote>>(
    `/quotes/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function sendQuote(
  id: number,
  input: SendQuoteInput,
  token: string
): Promise<ApiResponse<Quote>> {

  return apiRequest<ApiResponse<Quote>>(
    `/quotes/${ id }/send`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function convertQuote(
  id: number,
  input: ConvertQuoteToInvoiceInput,
  token: string
): Promise<ApiResponse<Invoice>> {

  return apiRequest<ApiResponse<Invoice>>(
    `/quotes/${ id }/convert`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export interface ListCustomersParams {
  limit?: number
  offset?: number
}

export async function listCustomers(
  params: ListCustomersParams = {},
  token: string
): Promise<PaginatedResponse<Customer>> {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Customer>>(`/customers${ qs }`, undefined, token);
}

export interface ListIssuerProfilesParams {
  limit?: number
  offset?: number
}

export async function listIssuerProfiles(
  params: ListIssuerProfilesParams = {},
  token: string
): Promise<PaginatedResponse<IssuerProfile>> {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<IssuerProfile>>(`/issuer-profiles${ qs }`, undefined, token);
}

export interface ListLineItemsParams {
  limit?: number
  offset?: number
}

export async function listLineItems(
  params: ListLineItemsParams = {},
  token: string
): Promise<PaginatedResponse<LineItem>> {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<LineItem>>(`/line-items${ qs }`, undefined, token);
}

export async function createCustomer(
  input: CreateCustomerInput,
  token: string
): Promise<ApiResponse<Customer>> {

  return apiRequest<ApiResponse<Customer>>(
    '/customers',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function createIssuerProfile(
  input: CreateIssuerProfileInput,
  token: string
): Promise<ApiResponse<IssuerProfile>> {

  return apiRequest<ApiResponse<IssuerProfile>>(
    '/issuer-profiles',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function getIssuerProfileById(
  id: number,
  token: string
): Promise<ApiResponse<IssuerProfile>> {

  return apiRequest<ApiResponse<IssuerProfile>>(`/issuer-profiles/${ id }`, undefined, token);
}

export async function updateIssuerProfile(
  id: number,
  input: UpdateIssuerProfileInput,
  token: string
): Promise<ApiResponse<IssuerProfile>> {

  return apiRequest<ApiResponse<IssuerProfile>>(
    `/issuer-profiles/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteIssuerProfile(
  id: number,
  token: string
): Promise<ApiResponse<void>> {

  return apiRequest<ApiResponse<void>>(
    `/issuer-profiles/${ id }`,
    {
      method: 'DELETE'
    },
    token
  );
}
